// src/pages/SuperAdmin/SuperAdminDashboard.tsx - Interface principale Super Admin (Design moderne avec Cards)

import React from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Modal,
  message,
  Switch,
  Empty,
  Spin,
  Dropdown,
  Menu,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  SettingOutlined,
  ShopOutlined,
  TrophyOutlined,
  TeamOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MoreOutlined,
  CalendarOutlined,
  WarningOutlined,
  SafetyOutlined,
  TagOutlined,
  ThunderboltOutlined,
  CrownOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { useAllBusinesses, useSuperAdminStats, useToggleBusinessStatus } from '../../hooks/useSuperAdmin';
import { useAllContractsWithPayments } from '../../hooks/useBusinessContracts';
import { hasPaymentDelay, isContractExpiringSoon } from '../../types/businessContract';
import { useDeleteBusiness } from '../../hooks/useBusinesses';
import { Business, BUSINESS_TYPE_CONFIGS } from '../../types/business';
import RelancesContratsCard from './components/RelancesContratsCard';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import relativeTime from 'dayjs/plugin/relativeTime';
import './SuperAdminDashboard.css';

dayjs.locale('fr');
dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const SuperAdminDashboard: React.FC = () => {
  const { switchToBusiness, switchToSuperAdmin, currentBusiness, userProfile, isCSM, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  // Données
  const { data: businesses = [], isLoading, refetch: refetchBusinesses } = useAllBusinesses();
  const { data: stats } = useSuperAdminStats();
  const { data: contractData } = useAllContractsWithPayments();

  const contractStatusByBusiness = React.useMemo(() => {
    const map = new Map<string, { paymentDelay: boolean; expiringSoon: boolean; hasContract: boolean }>();
    contractData?.forEach(({ contract, payments }) => {
      map.set(contract.businessId, {
        hasContract: true,
        paymentDelay: hasPaymentDelay(payments),
        expiringSoon: isContractExpiringSoon(contract, 60),
      });
    });
    return map;
  }, [contractData]);

  const toggleBusinessMutation = useToggleBusinessStatus();
  const deleteBusinessMutation = useDeleteBusiness();

  // Handlers
  const handleViewBusiness = async (business: Business) => {
    try {
      await switchToBusiness(business.id);
      message.success(`Connecté au business: ${business.name}`);
    } catch (error) {
      message.error('Erreur lors de la connexion au business');
      console.error(error);
    }
  };

  const handleBackToSuperAdmin = () => {
    switchToSuperAdmin();
    message.info('Retour au mode Super Admin');
  };

  const handleToggleStatus = async (businessId: string, currentStatus: boolean) => {
    try {
      await toggleBusinessMutation.mutateAsync({
        businessId,
        isActive: !currentStatus,
      });
      
      const action = !currentStatus ? 'activé' : 'désactivé';
      message.success(`Business ${action} avec succès`);
    } catch (error) {
      message.error('Erreur lors de la modification du statut');
      console.error(error);
    }
  };

  const handleDeleteBusiness = (business: Business) => {
    Modal.confirm({
      title: 'Supprimer définitivement ce business ?',
      content: (
        <div>
          <p>Cette action est <strong>irréversible</strong>.</p>
          <p>Le business "{business.name}" sera définitivement supprimé de la base de données.</p>
          <p style={{ color: '#ff4d4f', marginTop: '8px' }}>
            <strong>Attention :</strong> Cette opération ne peut être effectuée que sur un business archivé.
          </p>
        </div>
      ),
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          await deleteBusinessMutation.mutateAsync(business.id);
          message.success('Business supprimé définitivement');
          navigate('/super-admin/businesses');
          refetchBusinesses();
        } catch (error: any) {
          message.error(error.message || 'Erreur lors de la suppression du business');
          console.error(error);
        }
      },
    });
  };

  // Rendu d'une card business
  const renderBusinessCard = (business: Business) => {
    const isLinkedToBusiness = userProfile?.businessIds?.includes(business.id) ?? false;
    const canConnect = !isCSM || isLinkedToBusiness;

    const contractStatus = contractStatusByBusiness.get(business.id);

    const menu = (
      <Menu>
        {canConnect && (
          <Menu.Item 
            key="connect" 
            icon={<EyeOutlined />} 
            onClick={() => handleViewBusiness(business)}
          >
            Se connecter
          </Menu.Item>
        )}
        <Menu.Item 
          key="details" 
          icon={<SettingOutlined />} 
          onClick={() => navigate(`/super-admin/businesses/${business.id}`)}
        >
          Voir détails
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item 
          key="toggle" 
          icon={business.isActive ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
          onClick={() => handleToggleStatus(business.id, business.isActive)}
        >
          {business.isActive ? 'Désactiver' : 'Activer'}
        </Menu.Item>
      </Menu>
    );

    const typeConfig = BUSINESS_TYPE_CONFIGS[business.type];

    return (
      <Card 
        key={business.id}
        className="business-card"
        hoverable
        style={{
          marginBottom: '16px',
          borderLeft: `4px solid ${business.isActive ? '#52c41a' : '#d9d9d9'}`,
        }}
      >
        <Row gutter={16} align="middle">
          {/* Infos principales */}
          <Col xs={24} sm={24} md={14} lg={16}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              {/* En-tête */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Space size={8} wrap>
                    <ShopOutlined style={{ fontSize: '20px', color: '#722ed1' }} />
                    <Text strong style={{ fontSize: '16px' }}>{business.name}</Text>
                  </Space>
                  <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    <Tag color={typeConfig ? 'blue' : 'default'} icon={<TagOutlined />}>
                      {typeConfig?.name || business.type}
                    </Tag>
                    <Tag color={business.isActive ? 'green' : 'red'} icon={business.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                      {business.isActive ? 'Actif' : 'Inactif'}
                    </Tag>
                    {business.configurationInProgress && (
                      <Tag color="orange" icon={<WarningOutlined />}>
                        Config en cours
                      </Tag>
                    )}
                  </div>
                </div>

                {/* Switch statut */}
                <Switch
                  checked={business.isActive}
                  onChange={() => handleToggleStatus(business.id, business.isActive)}
                  loading={toggleBusinessMutation.isPending}
                  checkedChildren="Actif"
                  unCheckedChildren="Inactif"
                />
              </div>

              {/* Date de création */}
              <Space size={4}>
                <CalendarOutlined style={{ color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  Créé le {dayjs(business.createdAt).format('DD/MM/YYYY')}
                  <Text type="secondary" style={{ marginLeft: '8px' }}>
                    ({dayjs(business.createdAt).fromNow()})
                  </Text>
                </Text>
              </Space>

              {/* Statut contrat */}
              {contractStatus && (
                <div>
                  <Text type="secondary" style={{ fontSize: '13px', marginRight: '8px' }}>
                    Contrat:
                  </Text>
                  {contractStatus.paymentDelay && (
                    <Tag color="red" icon={<WarningOutlined />}>Retard paiement</Tag>
                  )}
                  {contractStatus.expiringSoon && !contractStatus.paymentDelay && (
                    <Tag color="orange" icon={<CalendarOutlined />}>À renouveler</Tag>
                  )}
                  {!contractStatus.paymentDelay && !contractStatus.expiringSoon && (
                    <Tag color="green" icon={<CheckCircleOutlined />}>À jour</Tag>
                  )}
                </div>
              )}

              {/* Informations de configuration */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                {business.branding?.phone && (
                  <Tag color="blue" icon={<PhoneOutlined />} style={{ margin: 0 }}>
                    Branding configuré
                  </Tag>
                )}
                {business.configuration?.inventory?.enabled && (
                  <Tag color="purple" icon={<ShopOutlined />} style={{ margin: 0 }}>
                    Stock activé
                  </Tag>
                )}
                {business.configuration?.loyalty?.enabled && (
                  <Tag color="gold" icon={<TrophyOutlined />} style={{ margin: 0 }}>
                    Cagnotte activée
                  </Tag>
                )}
                {business.configuration?.messagesClients && (
                  <Tag color="cyan" icon={<ThunderboltOutlined />} style={{ margin: 0 }}>
                    Messages activés
                  </Tag>
                )}
              </div>
            </Space>
          </Col>

          {/* Actions — menu contextuel uniquement */}
          <Col xs={24} sm={24} md={10} lg={8}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
              }}
            >
              <Dropdown overlay={menu} trigger={['click']}>
                <Button icon={<MoreOutlined />}>Actions</Button>
              </Dropdown>
            </div>
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <div className="super-admin-dashboard-container">
      {/* En-tête avec indicateur de mode */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <Title level={2} style={{ marginBottom: '8px' }}>
            <ShopOutlined /> Administration Générale
          </Title>
          {currentBusiness ? (
            <Space>
              <Tag color="orange" icon={<WarningOutlined />}>
                Mode Business: {currentBusiness.name}
              </Tag>
              <Button 
                type="link" 
                size="small" 
                onClick={handleBackToSuperAdmin}
                style={{ padding: 0 }}
              >
                Retour Super Admin
              </Button>
            </Space>
          ) : (
            <Text type="secondary" style={{ fontSize: '15px' }}>
              Gérez tous vos businesses depuis cette interface
            </Text>
          )}
        </div>
        
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={() => navigate('/super-admin/businesses/new')}
          style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
        >
          Créer un Business
        </Button>
      </div>

      {/* Statistiques */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card stat-total">
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <BankOutlined style={{ fontSize: '24px', color: 'white' }} />
              </div>
              <div className="stat-content">
                <Text type="secondary" style={{ fontSize: '13px' }}>Total Business</Text>
                <Title level={3} style={{ margin: 0 }}>{stats.totalBusinesses}</Title>
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card stat-active">
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                <TrophyOutlined style={{ fontSize: '24px', color: 'white' }} />
              </div>
              <div className="stat-content">
                <Text type="secondary" style={{ fontSize: '13px' }}>Actifs</Text>
                <Title level={3} style={{ margin: 0 }}>{stats.activeBusinesses}</Title>
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card stat-inactive">
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
                <TeamOutlined style={{ fontSize: '24px', color: 'white' }} />
              </div>
              <div className="stat-content">
                <Text type="secondary" style={{ fontSize: '13px' }}>Inactifs</Text>
                <Title level={3} style={{ margin: 0 }}>{stats.inactiveBusinesses}</Title>
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card className="stat-card stat-popular">
              <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                <CrownOutlined style={{ fontSize: '24px', color: 'white' }} />
              </div>
              <div className="stat-content">
                <Text type="secondary" style={{ fontSize: '13px' }}>Type populaire</Text>
                <Title level={3} style={{ margin: 0, fontSize: '18px' }}>
                  {Object.entries(stats.businessesByType || {})
                    .sort(([, a], [, b]) => (Number(b) - Number(a)))[0]?.[0] ?? 'N/A'}
                </Title>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Relances contrats (Super Admin / CSM) */}
      {(isSuperAdmin || isCSM) && (
        <div style={{ marginBottom: '24px' }}>
          <RelancesContratsCard />
        </div>
      )}

      {/* Compteur de résultats */}
      <div style={{ marginBottom: '16px' }}>
        <Text strong style={{ fontSize: '16px' }}>
          Liste des Business
        </Text>
        <Text type="secondary" style={{ marginLeft: '12px' }}>
          {businesses.length} business{businesses.length > 1 ? 'es' : ''}
        </Text>
      </div>

      {/* Liste des business en cards */}
      <Spin spinning={isLoading} size="large">
        {businesses.length === 0 ? (
          <Card>
            <Empty 
              description="Aucun business trouvé"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => navigate('/super-admin/businesses/new')}
                style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
              >
                Créer votre premier business
              </Button>
            </Empty>
          </Card>
        ) : (
          <div className="business-list">
            {businesses.map(business => renderBusinessCard(business))}
          </div>
        )}
      </Spin>
    </div>
  );
};

export default SuperAdminDashboard;
